import { ExternalLink, RefreshCw } from "lucide-react";
import { useCanAccess, useLocaleState, useTranslate } from "ra-core";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";

import { billingState } from "./billingState";
import {
  useBillingActions,
  useBillingStatus,
  useRefreshBilling,
} from "./useBilling";

const KNOWN_STATUSES = [
  "none",
  "active",
  "trialing",
  "past_due",
  "unpaid",
  "paused",
  "canceled",
  "incomplete",
  "incomplete_expired",
];

/**
 * « Abonnement » page (account menu, between Settings and Release notes): the
 * subscription for everyone; Stripe's portal and the refresh for administrators.
 */
export const SubscriptionPage = () => {
  const translate = useTranslate();
  const [locale = "fr"] = useLocaleState();
  const { canAccess: isAdministrator } = useCanAccess({
    resource: "configuration",
    action: "edit",
  });
  const { data: billing } = useBillingStatus();
  const { pay, manage, isOpening } = useBillingActions();
  const refresh = useRefreshBilling();
  const state = billingState(billing ?? null);
  const status = billing?.status ?? "none";

  const date = (value: string | null | undefined) =>
    value
      ? new Intl.DateTimeFormat(locale, { dateStyle: "long" }).format(
          new Date(value),
        )
      : null;
  const amount =
    billing?.amount != null && billing.currency
      ? new Intl.NumberFormat(locale, {
          style: "currency",
          currency: billing.currency.toUpperCase(),
        }).format(billing.amount / 100)
      : null;
  const rows = [
    { label: "crm.billing.plan", value: billing?.plan_name },
    { label: "crm.billing.amount", value: amount },
    {
      label: "crm.billing.next_payment",
      value: date(billing?.current_period_end),
    },
    { label: "crm.billing.ends_on", value: date(billing?.cancel_at) },
  ].filter((row) => row.value);

  return (
    <Card className="max-w-2xl mx-auto my-8">
      <CardContent className="flex flex-col gap-4">
        <div className="flex flex-wrap items-center gap-3">
          <h1 className="text-xl font-semibold flex-1">
            {translate("crm.billing.title")}
          </h1>
          <Badge variant={state.kind === "ok" ? "secondary" : "destructive"}>
            {translate(
              `crm.billing.statuses.${KNOWN_STATUSES.includes(status) ? status : "none"}`,
            )}
          </Badge>
        </div>
        {rows.length > 0 ? (
          <dl className="grid grid-cols-[auto_1fr] gap-x-6 gap-y-1 text-sm">
            {rows.map((row) => (
              <div key={row.label} className="contents">
                <dt className="text-muted-foreground">
                  {translate(row.label)}
                </dt>
                <dd>{row.value}</dd>
              </div>
            ))}
          </dl>
        ) : (
          <p className="text-sm text-muted-foreground">
            {translate("crm.billing.no_subscription")}
          </p>
        )}
        <div className="flex flex-wrap gap-2">
          {isAdministrator && billing?.stripe_customer_id ? (
            <Button
              type="button"
              variant="outline"
              disabled={isOpening}
              onClick={manage}
            >
              <ExternalLink className="size-4" />
              {translate("crm.billing.manage")}
            </Button>
          ) : null}
          {state.kind !== "ok" ? (
            <Button type="button" disabled={isOpening} onClick={pay}>
              {translate(
                state.kind === "activate"
                  ? "crm.billing.activate"
                  : "crm.billing.pay",
              )}
            </Button>
          ) : null}
          {isAdministrator ? (
            <Button
              type="button"
              variant="ghost"
              disabled={refresh.isPending}
              onClick={() => refresh.mutate()}
            >
              <RefreshCw className="size-4" />
              {translate("crm.billing.refresh")}
            </Button>
          ) : null}
        </div>
      </CardContent>
    </Card>
  );
};

SubscriptionPage.path = "/subscription";
